import { FileIcon, PlusIcon } from '@/assets/icons'
import React, { useRef } from 'react'
import toast from 'react-hot-toast'
import { FileInput } from '@components/Field'
import { AddButton, EmptyAttachments, SectionContainer, SectionHeader, SectionLabel } from './AttachmentSection.styles'
import { ATTACHMENT_ACCEPT, MAX_ATTACHMENTS, overCapMessage } from './attachmentTypes'
import PendingThumbnails from './PendingThumbnails'
import { iconSizes } from '@/theme/tokens'

interface PendingAttachmentPickerProps {
	files: File[]
	onChange: (files: File[]) => void
}

const PendingAttachmentPicker: React.FC<PendingAttachmentPickerProps> = ({ files, onChange }) => {
	const fileInputRef = useRef<HTMLInputElement>(null)

	const remaining = MAX_ATTACHMENTS - files.length

	const handleAddClick = () => {
		fileInputRef.current?.click()
	}

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files
		if (!selected || selected.length === 0) return

		const newFiles = Array.from(selected)
		if (newFiles.length > remaining) {
			toast.error(overCapMessage(remaining))
			if (fileInputRef.current) fileInputRef.current.value = ''
			return
		}

		onChange([...files, ...newFiles])
		if (fileInputRef.current) fileInputRef.current.value = ''
	}

	return (
		<SectionContainer>
			<SectionHeader>
				<SectionLabel>
					<FileIcon />
					Attachments {files.length > 0 && `(${files.length}/${MAX_ATTACHMENTS})`}
				</SectionLabel>
				{files.length > 0 && remaining > 0 && (
					<AddButton onClick={handleAddClick} type="button">
						<PlusIcon sx={{ fontSize: iconSizes.sm }} />
						Add
					</AddButton>
				)}
			</SectionHeader>

			<FileInput
				ref={fileInputRef}
				multiple
				accept={ATTACHMENT_ACCEPT}
				onChange={handleFileChange}
			/>

			{files.length === 0 ? (
				<EmptyAttachments type="button" onClick={handleAddClick}>
					<FileIcon sx={{ fontSize: iconSizes.xs }} />
					Add attachments
				</EmptyAttachments>
			) : (
				<PendingThumbnails files={files} onRemove={index => onChange(files.filter((_, i) => i !== index))} />
			)}
		</SectionContainer>
	)
}

export default PendingAttachmentPicker
